import type { MemoBlock } from '@/shared/templates/memos'
import type { MemoItem } from '@/shared/templates/memos/lib/renderMemo'

/**
 * The id of the copy of `documentId` that lives in `blockId`.
 *
 * A document the memo holds in several blocks is several items, and everything
 * that addresses one row of the preview - the drag targets, the keyword select,
 * the remove cross - addresses it by `id`. Derived from the block it was made
 * for so that the copies of one document are told apart by where they sit,
 * which is the only thing that distinguishes them at the moment they are made.
 * It is an opaque string from then on: a copy dragged into another block keeps
 * the id it was made with, since nothing reads the block out of it again.
 */
export function blockCopyId(documentId: string, blockId: string): string {
  return `${documentId}#${blockId}`
}

/**
 * One copy of `item` per block, each in its own block.
 *
 * This is what a template that puts its material in every block selects with:
 * the print PM lists the same planning under the spread and under the single
 * page, and which of the two it does not belong under is the editor's to take
 * out afterwards. Nothing but the id and the block differs between the copies,
 * so each carries its own keyword from the block it is in.
 */
export function copiesForBlocks(item: MemoItem, blocks: MemoBlock[]): MemoItem[] {
  return blocks.map((block) => ({
    ...item,
    id: blockCopyId(item.documentId, block.id),
    blockId: block.id
  }))
}

/** The selection with every copy of one document taken out. */
export function withoutDocument(selected: MemoItem[], documentId: string): MemoItem[] {
  return selected.filter((entry) => entry.documentId !== documentId)
}

/**
 * Whether `blockId` already holds a copy of `item`'s document that is not
 * `item` itself.
 *
 * A block listing the same document twice is a memo that says the same thing
 * twice, so this is what stops a copy being dragged onto its sibling's block,
 * and what a merged-away block's items are filtered through when the block they
 * are merged into already has them.
 */
export function holdsDocument(selected: MemoItem[], blockId: string, item: MemoItem): boolean {
  return selected.some((entry) => (
    entry.id !== item.id
    && entry.blockId === blockId
    && entry.documentId === item.documentId
  ))
}

/**
 * The selection with one item per document, the first of each kept.
 *
 * What a candidate column counts: its button asks how much of the column the
 * memo has taken, and a document taken into two blocks was taken once.
 */
export function distinctDocuments(selected: MemoItem[]): MemoItem[] {
  const seen = new Set<string>()

  return selected.filter((entry) => {
    if (seen.has(entry.documentId)) {
      return false
    }

    seen.add(entry.documentId)

    return true
  })
}
